import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ForgeTooltip } from '@tylertech/forge-react';
import './TableHierarchyGroupingHeader.scss';
import { I18n } from 'common/i18n';
import size from 'lodash/size';

// components
import TableHierarchyGroupingKebabMenu from './TableHierarchyGroupingKebabMenu';

//types
import { Hierarchy, HierarchyColumnConfig } from 'common/visualizations/vif';
import { ViewColumn } from 'common/types/viewColumn';
import { isTableAdvancedTotalingEnabled } from 'common/visualizations/helpers/VifSelectors';
import { TableColumnFormats } from 'common/visualizations/views/agGridReact/types';

// project constants
const scope = 'shared.visualizations.panes.data';

interface TableHierarchyGroupingHeaderProps {
  columnMetadata: ViewColumn[];
  hierarchy: Hierarchy;
  duplicationAllowed: boolean;
  removeAllowed: boolean;
  hierarchyGroupingColumnConfigs: HierarchyColumnConfig[];
  setActiveHierarchy: () => void;
  handleOnRemoveHierarchy: () => void;
  handleOnDuplicateHierarchy: () => void;
  handleOnUpdateHierarchyName: (name: string) => void;
  columnFormats: TableColumnFormats;
}

export const getGroupingColumnNames = (
  hierarchyGroupingColumnConfigs: HierarchyColumnConfig[],
  columnMetadata: ViewColumn[],
  columnFormats: TableColumnFormats = {},
) => {
  return hierarchyGroupingColumnConfigs.map((h: HierarchyColumnConfig) => {
    const displayName = columnFormats?.[h.columnName]?.displayName;
    if (displayName && isTableAdvancedTotalingEnabled()) {
      return displayName;
    }
    const column = columnMetadata.find(({ fieldName }) => h.columnName === fieldName);
    return column?.name ?? h.columnName;
  });
};

export const getHierarchyHeaderText = (
  hierarchyGroupingColumnConfigs: HierarchyColumnConfig[],
  columnMetadata: ViewColumn[],
  columnFormats: TableColumnFormats,
) => {
  let headerText = I18n.t('fields.table_hierarchies.header.ungrouped', { scope });

  const groupingColumnNames = getGroupingColumnNames(
    hierarchyGroupingColumnConfigs,
    columnMetadata,
    columnFormats,
  );

  if (size(groupingColumnNames)) {
    headerText = `${I18n.t('fields.table_hierarchies.header.by', { scope })} `;

    if (size(groupingColumnNames) > 2) {
      groupingColumnNames.forEach((name: string, idx: number) => {
        if (idx === groupingColumnNames.length - 1) {
          headerText += `${I18n.t('fields.table_hierarchies.header.and', { scope })} ${name}`;
        } else {
          headerText += `${name}, `;
        }
      });
    } else {
      headerText += groupingColumnNames.join(` ${I18n.t('fields.table_hierarchies.header.and', { scope })} `);
    }
  }

  return headerText;
};

export const hierarchyTabTitle = (
  hierarchy: Hierarchy,
  hierarchyGroupingColumnConfigs: HierarchyColumnConfig[],
  columnMetadata: ViewColumn[],
  columnFormats: TableColumnFormats = {},
) => {
  return (
    hierarchy.name || getHierarchyHeaderText(hierarchyGroupingColumnConfigs, columnMetadata, columnFormats)
  );
};

const TableHierarchyGroupingHeader = (props: TableHierarchyGroupingHeaderProps) => {
  const {
    columnMetadata = [],
    hierarchy,
    hierarchyGroupingColumnConfigs,
    duplicationAllowed,
    removeAllowed,
    setActiveHierarchy,
    handleOnRemoveHierarchy,
    handleOnDuplicateHierarchy,
    columnFormats,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [editingTitle, setEditingTitle] = useState<boolean>(false);

  const [hierarchyTitle, setHierarchyTitle] = useState<string>(
    hierarchyTabTitle(hierarchy, hierarchyGroupingColumnConfigs, columnMetadata, columnFormats),
  );

  const outsideClickHandler = (e: MouseEvent) => {
    if (inputRef.current) {
      if (editingTitle && !inputRef.current.contains(e.target as Node)) {
        handleHierarchyNameChange(hierarchyTitle);
      }
    }
  };

  useEffect(() => {
    document.body.addEventListener('click', outsideClickHandler);
    return () => {
      document.body.removeEventListener('click', outsideClickHandler);
    };
  });

  const memoizedHeaderText = useMemo(
    () => getHierarchyHeaderText(hierarchyGroupingColumnConfigs, columnMetadata, columnFormats),
    [hierarchyGroupingColumnConfigs, columnMetadata, columnFormats],
  );

  useEffect(() => {
    if (!hierarchy.name) {
      setHierarchyTitle(memoizedHeaderText);
    } else if (hierarchy.name !== hierarchyTitle) {
      setHierarchyTitle(hierarchy.name);
    }
  }, [hierarchy.name, hierarchyGroupingColumnConfigs, columnFormats]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const { code } = e;
    if (code === 'Space') {
      e.stopPropagation();
    }

    if (code === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      handleHierarchyNameChange(hierarchyTitle);
    }
  }

  // Stops inputField re-renders
  function handleClick(e: React.MouseEvent<HTMLInputElement>) {
    e.stopPropagation();
  }

  function handleHierarchyNameChange(newName: string) {
    props.handleOnUpdateHierarchyName(newName);
    setEditingTitle(false);
  }

  function handleOnClickEdit() {
    setEditingTitle(true);
  }

  function handleOnClickEditLegacy(e: React.MouseEvent<HTMLInputElement>) {
    e.stopPropagation();
    setEditingTitle(true);
  }

  function handleOnTitleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setHierarchyTitle(e.target.value);
  }

  // INFO: EN-85363 To be deprecated / removed once enable_table_advanced_totaling is set to true permanently
  const renderLegacyTableHierarchyGroupingHeader = () => (
    <div className="table-hierarchy-header">
      <div className="editable-hierarchy-title" onClick={() => setActiveHierarchy()}>
        <span className="icon socrata-icon-table-hierarchy" />
        {editingTitle ? (
          <input
            className="hierarchy-header-title-input-field"
            ref={inputRef}
            value={hierarchyTitle}
            onChange={handleOnTitleChange}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            autoFocus
          />
        ) : (
          <div className="hierarchy-title-container">
            <span className="hierarchy-title">{hierarchyTitle}</span>
            <ForgeTooltip placement="bottom-start">
              <div className="tooltip-content">{hierarchyTitle}</div>
            </ForgeTooltip>
            <span role="button" className="icon socrata-icon-edit" onClick={handleOnClickEditLegacy} />
          </div>
        )}
      </div>
      <TableHierarchyGroupingKebabMenu
        duplicationAllowed={duplicationAllowed}
        removeAllowed={removeAllowed}
        handleOnDuplicateHierarchy={handleOnDuplicateHierarchy}
        handleOnRemoveHierarchy={handleOnRemoveHierarchy}
      />
    </div>
  );

  return isTableAdvancedTotalingEnabled() ? (
    <div className="table-hierarchy--header">
      <div className="editable-hierarchy-title" onClick={() => setActiveHierarchy()}>
        {editingTitle ? (
          <input
            className="hierarchy-header-title-input-field"
            ref={inputRef}
            value={hierarchyTitle}
            onChange={handleOnTitleChange}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            autoFocus
          />
        ) : (
          <div className="hierarchy-title-container">
            <span>{hierarchyTitle}</span>
            <ForgeTooltip placement="bottom-start">
              <div className="tooltip-content">{hierarchyTitle}</div>
            </ForgeTooltip>
          </div>
        )}
      </div>
      <TableHierarchyGroupingKebabMenu
        duplicationAllowed={duplicationAllowed}
        removeAllowed={removeAllowed}
        handleRename={handleOnClickEdit}
        handleOnDuplicateHierarchy={handleOnDuplicateHierarchy}
        handleOnRemoveHierarchy={handleOnRemoveHierarchy}
      />
    </div>
  ) : (
    renderLegacyTableHierarchyGroupingHeader()
  );
};

export default TableHierarchyGroupingHeader;
